import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Camera, Upload, Download, Eraser, Check, RefreshCw, ChevronRight,
  FileText, MapPin, MessageSquare, StickyNote, ArrowLeft, Package, Truck,
} from "lucide-react";
import PodGallery from "@/components/ui/PodGallery";
import { DatePicker } from "@/components/ui/date-picker";
import { formatDate, formatDateTime } from "@/lib/utils";

const STATUS = {
  assigned: { c: "#19e6c8", l: "Assigned" },
  pending:  { c: "#19e6c8", l: "Assigned" }, // backward compat
  onboard:  { c: "#facc15", l: "Onboard" },
  done:     { c: "#22c55e", l: "Done" },
  futile:   { c: "#ef4444", l: "Futile" },
};

// ETA is a delivery DATE + a 2-hour WINDOW rather than an exact time — a
// dropdown is far easier to tap than a combined date+time picker on mobile.
// Keep in sync with the copies in JobStatus.jsx and ClientJobs.jsx.
const ETA_WINDOWS = [
  { value: "06:00-08:00", label: "6:00 AM – 8:00 AM" },
  { value: "08:00-10:00", label: "8:00 AM – 10:00 AM" },
  { value: "10:00-12:00", label: "10:00 AM – 12:00 PM" },
  { value: "12:00-14:00", label: "12:00 PM – 2:00 PM" },
  { value: "14:00-16:00", label: "2:00 PM – 4:00 PM" },
  { value: "16:00-18:00", label: "4:00 PM – 6:00 PM" },
  { value: "18:00-20:00", label: "6:00 PM – 8:00 PM" },
];

export default function DriverRuns() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useState("current"); // "current" | "completed"
  // Single view state replaces all modal states. A job opens as its own full page:
  // null | { type: 'jobDetail'|'pickupAction'|'deliveryAction'|'pickupSignature'|'pod'|'legComplete'|'reupload', job, kind?, replace?, legIndex? }
  const [view, setView] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/jobs");
      setJobs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  // Poll in the background so newly-attached documents, admin remarks, etc.
  // show up on an assigned job without the driver having to force-reload the
  // app — a silent refresh (no toast/loading flicker) every 30s.
  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const back = () => setView(null);
  // Sub-action views are launched from a job's detail page — return there, not to the list.
  const toDetail = (job) => setView({ type: "jobDetail", job });

  const logPickupArrived = async (id) => {
    try {
      await api.patch(`/jobs/${id}/pickup-arrived`, {});
      toast.success("Pickup arrival recorded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const logDeliveryArrived = async (id) => {
    try {
      await api.patch(`/jobs/${id}/delivery-arrived`, {});
      toast.success("Delivery arrival recorded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Opens an e-doc in a new tab rather than force-downloading it — the file
  // still comes from an authenticated fetch (no public URL to hang a plain
  // <a href> off), so the tab is opened synchronously inside the click
  // handler and pointed at the blob once it's ready. Opening only after the
  // await breaks the user-gesture chain and mobile browsers (iOS Safari in
  // particular) silently strand it on about:blank instead of navigating.
  const downloadEDoc = async (path) => {
    const w = window.open("", "_blank", "noopener,noreferrer");
    try {
      const token = localStorage.getItem("df_token");
      const res = await fetch(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (!w) { toast.error("Please allow pop-ups to view documents"); return; }
      w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      if (w) w.close();
      toast.error(e.message);
    }
  };

  // ── Full-page view routing ───────────────────────────────────────────────────
  if (view) {
    if (view.type === "pickupAction") {
      // Use the live job from state so the arrival confirmation updates in place
      // after "Arrived at Pickup" without navigating away.
      const liveJob = jobs.find((x) => x.id === view.job.id) || view.job;
      return (
        <PickupActionView
          job={liveJob}
          onBack={() => toDetail(view.job)}
          onArrived={() => logPickupArrived(view.job.id)}
          onComplete={() => setView({ type: "pickupSignature", job: view.job })}
        />
      );
    }
    if (view.type === "deliveryAction") {
      const liveJob = jobs.find((x) => x.id === view.job.id) || view.job;
      return (
        <DeliveryActionView
          job={liveJob}
          onBack={() => toDetail(view.job)}
          onArrived={() => logDeliveryArrived(view.job.id)}
          onComplete={() => setView({ type: "pod", job: view.job, replace: false })}
        />
      );
    }
    if (view.type === "pickupSignature") {
      return (
        <PickupSignatureView
          job={view.job}
          onBack={() => setView({ type: "pickupAction", job: view.job })}
          onDone={() => { toDetail(view.job); load(); }}
        />
      );
    }
    if (view.type === "pod") {
      return (
        <PodView
          job={view.job}
          replace={view.replace || false}
          onBack={view.replace
            ? () => toDetail(view.job)
            : () => setView({ type: "deliveryAction", job: view.job })}
          onDone={() => { toDetail(view.job); load(); }}
        />
      );
    }
    if (view.type === "legComplete") {
      return (
        <LegCompleteView
          job={view.job}
          legIndex={view.legIndex}
          onBack={() => toDetail(view.job)}
          onDone={() => { toDetail(view.job); load(); }}
        />
      );
    }
    if (view.type === "reupload") {
      return (
        <ReuploadView
          job={view.job}
          kind={view.kind}
          onBack={() => toDetail(view.job)}
          onDone={() => { toDetail(view.job); load(); }}
        />
      );
    }
    if (view.type === "jobDetail") {
      return renderJobDetail(view.job);
    }
  }

  // ── Job list ─────────────────────────────────────────────────────────────────
  const currentJobs = jobs.filter((j) => ["assigned", "pending", "onboard"].includes(j.status));
  const finishedJobs = jobs.filter((j) => ["done", "futile"].includes(j.status));

  // Compact list card — clicking opens the run in its own full-page view.
  const renderJobCard = (j) => {
    const m = STATUS[j.status] || STATUS.pending;

    return (
      <button
        key={j.id}
        onClick={() => setView({ type: "jobDetail", job: j })}
        className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.02] px-4 sm:px-5 py-4 flex items-start gap-3 hover:bg-white/[0.04] transition-colors"
        data-testid={`run-${j.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="brand-title text-base text-[#19e6c8]">{j.job_ref}</h3>
            <span className="text-white/40 text-xs">{formatDate(j.job_date)}</span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ background: m.c + "22", color: m.c }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.c }} /> {m.l}
            </span>
            {j.legs?.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}>
                +{j.legs.length} stop{j.legs.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-2">
            <div className="flex items-start gap-1.5 text-sm">
              <MapPin size={13} className="mt-0.5 shrink-0 text-white/30" />
              <div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest block">Pickup</span>
                <span className="text-white/85 text-xs leading-tight">{j.pickup_address}</span>
              </div>
            </div>
            <div className="flex items-start gap-1.5 text-sm">
              <MapPin size={13} className="mt-0.5 shrink-0 text-white/30" />
              <div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest block">Drop-off</span>
                <span className="text-white/85 text-xs leading-tight">{j.dropoff_address}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="shrink-0 mt-1 text-white/40">
          <ChevronRight size={18} />
        </div>
      </button>
    );
  };

  // Full-page detail for a single run. Uses the live job from state so it stays
  // fresh after pickup/delivery/re-upload actions. Declared as a hoisted function
  // so the view-routing block above can call it before this line.
  function renderJobDetail(jobArg) {
    const j = jobs.find((x) => x.id === jobArg.id) || jobArg;
    const m = STATUS[j.status] || STATUS.pending;
    const hasPod = j.pod_image_urls?.length > 0 || j.signature_url;
    const hasPickupSig = !!j.sender_signature_url;

    return (
      <div className="fade-up" data-testid={`run-detail-${j.id}`}>
        <PageHeader onBack={back} title={j.job_ref} subtitle={`${formatDate(j.job_date)} · ${m.l}`} />
        <div className="max-w-2xl">
          {/* Addresses */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-1.5 text-sm">
              <MapPin size={14} className="mt-0.5 shrink-0 text-[#19e6c8]" />
              <div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest block">Pickup</span>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(j.pickup_address)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-white/85 hover:text-[#19e6c8] underline decoration-dotted text-sm leading-tight">
                  {j.pickup_address}
                </a>
                {(j.pickup_contact_name || j.pickup_contact_phone) && (
                  <p className="text-xs text-white/50 mt-0.5">
                    {[j.pickup_contact_name, j.pickup_contact_phone].filter(Boolean).join(" · ")}
                  </p>
                )}
                {j.pickup_operating_hours && (
                  <p className="text-xs mt-0.5" style={{ color: "#facc15" }}>Hours: {j.pickup_operating_hours}</p>
                )}
                {j.pickup_address_notes && (
                  <p className="text-xs mt-0.5 text-white/50 italic">"{j.pickup_address_notes}"</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-1.5 text-sm">
              <MapPin size={14} className="mt-0.5 shrink-0 text-[#22c55e]" />
              <div>
                <span className="text-[10px] text-white/40 uppercase tracking-widest block">Drop-off</span>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(j.dropoff_address)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-white/85 hover:text-[#22c55e] underline decoration-dotted text-sm leading-tight">
                  {j.dropoff_address}
                </a>
                {(j.dropoff_contact_name || j.dropoff_contact_phone) && (
                  <p className="text-xs text-white/50 mt-0.5">
                    {[j.dropoff_contact_name, j.dropoff_contact_phone].filter(Boolean).join(" · ")}
                  </p>
                )}
                {j.dropoff_operating_hours && (
                  <p className="text-xs mt-0.5" style={{ color: "#facc15" }}>Hours: {j.dropoff_operating_hours}</p>
                )}
                {j.dropoff_address_notes && (
                  <p className="text-xs mt-0.5 text-white/50 italic">"{j.dropoff_address_notes}"</p>
                )}
              </div>
            </div>
          </div>

            {/* Additional pickups/deliveries/stops — surfaced right after the
                primary addresses (not buried below the action buttons) so a
                driver sees at a glance whether this run has extra stops. */}
            {j.legs?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-white/50 uppercase tracking-widest mb-3">
                  Additional Pickups / Deliveries / Stops ({j.legs.length})
                </p>
                <div className="space-y-2">
                  {j.legs.map((leg, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-semibold truncate">{leg.address}</p>
                        <p className="text-xs text-white/40 capitalize">{leg.stop_type}{leg.notes ? ` · ${leg.notes}` : ""}</p>
                        {(leg.contact_name || leg.contact_phone) && (
                          <p className="text-xs text-white/50 mt-0.5">
                            {[leg.contact_name, leg.contact_phone].filter(Boolean).join(" · ")}
                          </p>
                        )}
                      </div>
                      {leg.completed_at ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 shrink-0">Done</span>
                      ) : (
                        <button onClick={() => setView({ type: "legComplete", job: j, legIndex: i })}
                          className="shrink-0 px-3 py-2 rounded-xl text-xs font-semibold"
                          style={{ background: "rgba(25,230,200,0.12)", color: "#19e6c8" }}
                          data-testid={`complete-leg-${j.id}-${i}`}>
                          Complete
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Item details */}
            <div className="mt-4 text-sm text-white/70">
              {j.item_qty} × {j.item_type} · {j.volume_cbm} cbm · {j.weight_kgs} kgs
            </div>
            {j.notes && <div className="mt-1 text-sm text-white/60 italic">"{j.notes}"</div>}

            {/* Admin remarks */}
            {j.admin_remarks && (
              <div className="mt-4 px-4 py-3 rounded-xl"
                style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.2)" }}>
                <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: "#f59e0b" }}>
                  Notes from Admin
                </p>
                <p className="text-sm text-white/80">{j.admin_remarks}</p>
              </div>
            )}

            {/* Push ETA — lets the driver update the customer directly (e.g.
                running late/early), same as admin can from Job Status. */}
            {!["done", "futile"].includes(j.status) && (
              <PushEtaCard job={j} onDone={load} />
            )}

            {/* E-Docs */}
            {j.e_doc_files?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-white/50 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                  <FileText size={12} /> E-Documents
                </p>
                <div className="space-y-2">
                  {j.e_doc_files.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white/80 truncate">{doc.name}</div>
                        <div className="text-[10px] text-white/40">{doc.doc_type || "Document"}{doc.uploaded_by === "client" ? " · from client" : ""}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => downloadEDoc(doc.path)}
                          className="px-2.5 py-2 rounded-lg text-xs font-semibold flex items-center gap-1"
                          style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}>
                          <Download size={11} /> Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-5 grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              {/* Onboard & Delivery are hidden once the run is Delivered (done) or Futile —
                  the driver can only re-upload photos/notes from the sections below. */}
              {!["done", "futile"].includes(j.status) && (
                <>
                  <button onClick={() => setView({ type: "pickupAction", job: j })}
                    className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl text-sm font-semibold"
                    style={{ background: "#facc1533", color: "#facc15" }}
                    data-testid={`onboard-${j.id}`}>
                    <Package size={15} /> Onboard
                  </button>
                  <button onClick={() => setView({ type: "deliveryAction", job: j })}
                    className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl text-sm font-semibold"
                    style={{ background: "#22c55e33", color: "#22c55e" }}
                    data-testid={`pod-${j.id}`}>
                    <Truck size={15} /> Delivery
                  </button>
                </>
              )}
              <button
                onClick={() => navigate(`/driver-dashboard/messages?jobRef=${encodeURIComponent(j.job_ref)}`)}
                className="col-span-2 sm:col-auto flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(59,130,246,0.15)", color: "#3b82f6" }}
                data-testid={`query-job-${j.id}`}>
                <MessageSquare size={14} /> Query a Job
              </button>
            </div>

            {/* Pickup confirmation — stays visible after delivery so pickup photos/notes can be re-uploaded */}
            {hasPickupSig && (
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-white/50 uppercase tracking-widest">Pickup Confirmation</span>
                  <button onClick={() => setView({ type: "reupload", job: j, kind: "pickup" })}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
                    style={{ background: "rgba(111,76,255,0.15)", color: "#a78bfa" }}
                    data-testid={`re-upload-pickup-${j.id}`}>
                    <RefreshCw size={12} /> Re-upload
                  </button>
                </div>
                <PodGallery imageUrls={j.onboard_image_urls || []} senderSignatureUrl={j.sender_signature_url} />
              </div>
            )}

            {/* POD */}
            {hasPod && (
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-white/50 uppercase tracking-widest">Proof of Delivery</span>
                  <button onClick={() => setView({ type: "reupload", job: j, kind: "delivery" })}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
                    style={{ background: "rgba(111,76,255,0.15)", color: "#a78bfa" }}
                    data-testid={`re-upload-${j.id}`}>
                    <RefreshCw size={12} /> Re-upload
                  </button>
                </div>
                <PodGallery
                  imageUrls={j.pod_image_urls || []}
                  senderSignatureUrl={j.sender_signature_url}
                  signatureUrl={j.signature_url}
                  podCompletedAt={j.pod_completed_at}
                />
              </div>
            )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="driver-runs-page">
      <h1 className="brand-title text-2xl sm:text-3xl mb-1">My Runs</h1>
      <p className="text-white/60 text-sm mb-6">Welcome, {user.name}. Update each run as you complete it.</p>

      {/* ── Tabs ── */}
      <div className="flex gap-2 mb-6 p-1 rounded-2xl bg-white/[0.03] border border-white/10 w-fit">
        <button
          onClick={() => setTab("current")}
          className="px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
          style={tab === "current"
            ? { background: "rgba(245,158,11,0.15)", color: "#f59e0b" }
            : { color: "rgba(255,255,255,0.5)" }}
          data-testid="tab-current">
          Current{currentJobs.length > 0 ? ` (${currentJobs.length})` : ""}
        </button>
        <button
          onClick={() => setTab("completed")}
          className="px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
          style={tab === "completed"
            ? { background: "rgba(34,197,94,0.15)", color: "#22c55e" }
            : { color: "rgba(255,255,255,0.5)" }}
          data-testid="tab-completed">
          Completed{finishedJobs.length > 0 ? ` (${finishedJobs.length})` : ""}
        </button>
      </div>

      {/* ── Current Jobs ── */}
      {tab === "current" && (
        <div className="grid gap-3">
          {currentJobs.length === 0 && (
            <div className="text-white/50 text-sm py-3 px-4 rounded-xl border border-white/5 bg-white/[0.02]">
              No active runs assigned.
            </div>
          )}
          {currentJobs.map(renderJobCard)}
        </div>
      )}

      {/* ── Completed Jobs ── */}
      {tab === "completed" && (
        <div className="grid gap-3">
          {finishedJobs.length === 0 && (
            <div className="text-white/50 text-sm py-3 px-4 rounded-xl border border-white/5 bg-white/[0.02]">
              No completed jobs yet.
            </div>
          )}
          {finishedJobs.map(renderJobCard)}
        </div>
      )}
    </div>
  );
}

// ── Image compression ─────────────────────────────────────────────────────────
async function compressImage(file, maxDim = 1200, quality = 0.75) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" })),
        "image/jpeg", quality
      );
    };
    img.src = url;
  });
}

// ── Signature canvas hook ─────────────────────────────────────────────────────
function useSignatureCanvas(sigRef) {
  const drawing = useRef(false);
  useEffect(() => {
    const cv = sigRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.strokeStyle = "#111"; ctx.lineWidth = 2.5; ctx.lineCap = "round";
    const pos = (e) => {
      const r = cv.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
      return { x: x * (cv.width / r.width), y: y * (cv.height / r.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { drawing.current = false; };
    cv.addEventListener("mousedown", start); cv.addEventListener("mousemove", move);
    cv.addEventListener("mouseup", end); cv.addEventListener("mouseleave", end);
    cv.addEventListener("touchstart", start, { passive: false });
    cv.addEventListener("touchmove", move, { passive: false });
    cv.addEventListener("touchend", end);
    return () => {
      cv.removeEventListener("mousedown", start); cv.removeEventListener("mousemove", move);
      cv.removeEventListener("mouseup", end); cv.removeEventListener("mouseleave", end);
      cv.removeEventListener("touchstart", start); cv.removeEventListener("touchmove", move);
      cv.removeEventListener("touchend", end);
    };
  }, []);
  const clear = () => {
    const cv = sigRef.current;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, cv.width, cv.height);
  };
  return { clear };
}

// ── Shared back header ────────────────────────────────────────────────────────
function PageHeader({ onBack, title, subtitle }) {
  return (
    <div className="mb-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors mb-4"
      >
        <ArrowLeft size={16} /> Back
      </button>
      <h2 className="brand-title text-2xl sm:text-3xl">{title}</h2>
      {subtitle && <p className="text-white/55 text-sm mt-1">{subtitle}</p>}
    </div>
  );
}

// ── Push ETA — inline card inside the job detail view ─────────────────────────
function PushEtaCard({ job, onDone }) {
  const [etaDate, setEtaDate] = useState(job.eta_date || job.job_date || "");
  const [etaWindow, setEtaWindow] = useState(job.eta_window || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!etaDate || !etaWindow) { toast.error("Pick a date and a delivery window"); return; }
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}/push-eta`, { eta_date: etaDate, eta_window: etaWindow });
      toast.success("ETA pushed to customer");
      onDone && onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const currentLabel = job.eta_date && job.eta_window
    ? `${formatDate(job.eta_date)} · ${ETA_WINDOWS.find((w) => w.value === job.eta_window)?.label || job.eta_window}`
    : null;

  return (
    <div className="mt-4 pt-4 border-t border-white/10">
      <p className="text-xs text-white/50 uppercase tracking-widest mb-2">Push ETA to Customer</p>
      <div className="flex gap-2 flex-wrap">
        <DatePicker value={etaDate} onChange={setEtaDate} className="flex-1 min-w-[9rem]" />
        <select
          value={etaWindow}
          onChange={(e) => setEtaWindow(e.target.value)}
          className="flex-1 min-w-[9rem] bg-white/5 border border-white/20 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#19e6c8]/50"
        >
          <option value="" className="bg-[#15151f]">Select window</option>
          {ETA_WINDOWS.map((w) => (
            <option key={w.value} value={w.value} className="bg-[#15151f]">{w.label}</option>
          ))}
        </select>
        <button type="button" onClick={submit} disabled={busy}
          className="px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap"
          style={{ background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}
          data-testid={`push-eta-${job.id}`}>
          {busy ? "..." : "Push ETA"}
        </button>
      </div>
      {currentLabel && <p className="text-xs text-white/40 mt-2">Current: {currentLabel}</p>}
    </div>
  );
}

// ── Pickup Action View ────────────────────────────────────────────────────────
function PickupActionView({ job, onBack, onArrived, onComplete }) {
  return (
    <div className="fade-up" data-testid="pickup-action-page">
      <PageHeader
        onBack={onBack}
        title="Pickup"
        subtitle={`${job.job_ref} · Actions at pickup location`}
      />

      {/* Job address card */}
      <div className="mb-6 px-5 py-4 rounded-2xl border border-white/10 bg-white/[0.03]">
        <p className="text-xs text-white/40 uppercase tracking-widest mb-1">Pickup Address</p>
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(job.pickup_address)}`}
          target="_blank" rel="noopener noreferrer"
          className="text-white font-semibold text-base hover:text-[#19e6c8] underline decoration-dotted flex items-start gap-2"
        >
          <MapPin size={16} className="shrink-0 mt-0.5 text-[#19e6c8]" />
          {job.pickup_address}
        </a>
        <p className="text-xs text-white/40 mt-2">{job.item_qty} × {job.item_type}</p>
      </div>

      {/* Action buttons */}
      <div className="space-y-3 max-w-lg">
        {job.pickup_arrived_at ? (
          <div className="px-5 py-4 rounded-2xl border flex items-center gap-3"
            style={{ background: "rgba(25,230,200,0.08)", borderColor: "rgba(25,230,200,0.25)" }}>
            <Check size={20} className="text-[#19e6c8] shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#19e6c8]">Arrived at Pickup</p>
              <p className="text-xs text-white/50 mt-0.5">
                {formatDateTime(job.pickup_arrived_at)}
              </p>
            </div>
          </div>
        ) : (
          <button onClick={onArrived}
            className="w-full px-5 py-5 rounded-2xl text-base font-semibold flex items-center gap-4 transition-opacity hover:opacity-90 active:scale-[0.98]"
            style={{ background: "rgba(25,230,200,0.12)", color: "#19e6c8", border: "1px solid rgba(25,230,200,0.2)" }}>
            <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(25,230,200,0.15)" }}>
              <MapPin size={22} />
            </span>
            <div className="text-left">
              <p className="text-base font-bold">Arrived at Pickup</p>
              <p className="text-xs opacity-70 font-normal mt-0.5">Record your arrival time</p>
            </div>
          </button>
        )}

        <button onClick={onComplete}
          className="w-full px-5 py-5 rounded-2xl text-base font-semibold flex items-center gap-4 transition-opacity hover:opacity-90 active:scale-[0.98]"
          style={{ background: "rgba(250,204,21,0.12)", color: "#facc15", border: "1px solid rgba(250,204,21,0.2)" }}>
          <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(250,204,21,0.15)" }}>
            <Package size={22} />
          </span>
          <div className="text-left">
            <p className="text-base font-bold">Complete Pickup</p>
            <p className="text-xs opacity-70 font-normal mt-0.5">Collect photos &amp; sender signature</p>
          </div>
        </button>
      </div>
    </div>
  );
}

// ── Delivery Action View ──────────────────────────────────────────────────────
function DeliveryActionView({ job, onBack, onArrived, onComplete }) {
  return (
    <div className="fade-up" data-testid="delivery-action-page">
      <PageHeader
        onBack={onBack}
        title="Delivery"
        subtitle={`${job.job_ref} · Actions at delivery location`}
      />

      {/* Job address card */}
      <div className="mb-6 px-5 py-4 rounded-2xl border border-white/10 bg-white/[0.03]">
        <p className="text-xs text-white/40 uppercase tracking-widest mb-1">Drop-off Address</p>
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(job.dropoff_address)}`}
          target="_blank" rel="noopener noreferrer"
          className="text-white font-semibold text-base hover:text-[#22c55e] underline decoration-dotted flex items-start gap-2"
        >
          <MapPin size={16} className="shrink-0 mt-0.5 text-[#22c55e]" />
          {job.dropoff_address}
        </a>
        <p className="text-xs text-white/40 mt-2">{job.item_qty} × {job.item_type}</p>
      </div>

      {/* Action buttons */}
      <div className="space-y-3 max-w-lg">
        {job.delivery_arrived_at ? (
          <div className="px-5 py-4 rounded-2xl border flex items-center gap-3"
            style={{ background: "rgba(34,197,94,0.08)", borderColor: "rgba(34,197,94,0.25)" }}>
            <Check size={20} className="text-[#22c55e] shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#22c55e]">Arrived at Delivery</p>
              <p className="text-xs text-white/50 mt-0.5">
                {formatDateTime(job.delivery_arrived_at)}
              </p>
            </div>
          </div>
        ) : (
          <button onClick={onArrived}
            className="w-full px-5 py-5 rounded-2xl text-base font-semibold flex items-center gap-4 transition-opacity hover:opacity-90 active:scale-[0.98]"
            style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.2)" }}>
            <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(34,197,94,0.15)" }}>
              <MapPin size={22} />
            </span>
            <div className="text-left">
              <p className="text-base font-bold">Arrived at Delivery</p>
              <p className="text-xs opacity-70 font-normal mt-0.5">Record your arrival time</p>
            </div>
          </button>
        )}

        <button onClick={onComplete}
          className="w-full px-5 py-5 rounded-2xl text-base font-semibold flex items-center gap-4 transition-opacity hover:opacity-90 active:scale-[0.98]"
          style={{ background: "rgba(59,130,246,0.12)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.2)" }}>
          <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(59,130,246,0.15)" }}>
            <Truck size={22} />
          </span>
          <div className="text-left">
            <p className="text-base font-bold">Complete Delivery</p>
            <p className="text-xs opacity-70 font-normal mt-0.5">Upload POD photos &amp; receiver signature</p>
          </div>
        </button>
      </div>
    </div>
  );
}

// ── Pickup Signature View ─────────────────────────────────────────────────────
function PickupSignatureView({ job, onBack, onDone }) {
  const [busy, setBusy] = useState(false);
  const [onboardFiles, setOnboardFiles] = useState([]);
  const [senderName, setSenderName] = useState("");
  const sigRef = useRef(null);
  const { clear: clearSig } = useSignatureCanvas(sigRef);

  const onPickOnboard = (e) => {
    const sel = Array.from(e.target.files || []).slice(0, 4 - onboardFiles.length);
    setOnboardFiles((f) => [...f, ...sel].slice(0, 4));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of onboardFiles) fd.append("files", await compressImage(f));
      fd.append("signature_data_url", sigRef.current.toDataURL("image/png"));
      if (senderName.trim()) fd.append("sender_name", senderName.trim());
      await api.post(`/jobs/${job.id}/pickup-signature`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pickup confirmed");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" data-testid="pickup-signature-page">
      <PageHeader
        onBack={onBack}
        title="Confirm Pickup"
        subtitle={`${job.job_ref} · Document condition & collect sender signature`}
      />

      <div className="max-w-lg space-y-5">
        {/* Photos */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <p className="text-white/85 mb-3 font-semibold text-sm">Condition Photos ({onboardFiles.length}/4)</p>
          <div className="grid grid-cols-4 gap-2">
            {onboardFiles.map((f, i) => (
              <div key={i} className="relative aspect-square">
                <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover rounded-xl border border-white/10" />
                <button onClick={() => setOnboardFiles((arr) => arr.filter((_, idx) => idx !== i))}
                  className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center shadow-lg">×</button>
              </div>
            ))}
            {onboardFiles.length < 4 && (
              <>
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-amber-400/60 hover:text-amber-400/60 transition-colors">
                  <Camera size={22} /><span className="text-[10px] mt-1 font-semibold">Camera</span>
                  <input type="file" accept="image/*" capture="environment" hidden onChange={onPickOnboard} data-testid="onboard-camera" />
                </label>
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-amber-400/60 hover:text-amber-400/60 transition-colors">
                  <Upload size={22} /><span className="text-[10px] mt-1 font-semibold">Upload</span>
                  <input type="file" accept="image/*" multiple hidden onChange={onPickOnboard} data-testid="onboard-upload" />
                </label>
              </>
            )}
          </div>
        </div>

        {/* Sender Name */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <label className="block text-white/70 font-semibold mb-2 text-sm">Sender Name</label>
          <input value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Person handing goods"
            className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-amber-400/50 text-sm" data-testid="sender-name" />
        </div>

        {/* Signature */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/85 font-semibold text-sm">Sender Signature</p>
            <button onClick={clearSig} className="text-xs text-white/50 hover:text-white flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-white/10" data-testid="clear-sender-sig">
              <Eraser size={13} /> Clear
            </button>
          </div>
          <canvas ref={sigRef} width={600} height={220}
            className="w-full rounded-xl bg-white border-2 border-white/20 cursor-crosshair touch-none"
            data-testid="sender-signature-pad" />
          <p className="text-[10px] text-white/30 mt-1.5 text-center">Draw signature above</p>
        </div>

        {/* Submit */}
        <button onClick={submit} disabled={busy}
          className="w-full px-5 py-4 rounded-2xl text-base font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
          style={{ background: "linear-gradient(90deg, #f59e0b, #facc15)", color: "#1a1a2e" }}
          data-testid="submit-pickup">
          <Check size={18} /> {busy ? "Submitting…" : "Confirm Pickup"}
        </button>
      </div>
    </div>
  );
}

// ── Leg Complete View ─────────────────────────────────────────────────────────
function LegCompleteView({ job, legIndex, onBack, onDone }) {
  const [files, setFiles] = useState([]);
  const [signerName, setSignerName] = useState("");
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);
  const { clear: clearSig } = useSignatureCanvas(sigRef);
  const leg = job.legs?.[legIndex] || {};
  // A pickup-type leg is signed by a sender, a dropoff-type leg by a receiver —
  // label the capture screen accordingly instead of the generic "Stop N".
  const stopMeta = {
    pickup:  { title: "Complete Pickup",  nameLabel: "Sender Name",   namePlaceholder: "Name of the sender at this stop",   doneLabel: "Complete Pickup",  toast: "Pickup completed" },
    dropoff: { title: "Complete Delivery", nameLabel: "Receiver Name", namePlaceholder: "Name of the receiver at this stop", doneLabel: "Complete Delivery", toast: "Delivery completed" },
  }[leg.stop_type] || {
    title: `Complete Stop ${legIndex + 1}`, nameLabel: "Signer Name", namePlaceholder: "Name of person at this stop",
    doneLabel: "Complete Stop", toast: "Stop completed",
  };

  const onPick = (e) => {
    const sel = Array.from(e.target.files || []).slice(0, 8 - files.length);
    setFiles((f) => [...f, ...sel].slice(0, 8));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", await compressImage(f));
      fd.append("signature_data_url", sigRef.current.toDataURL("image/png"));
      if (signerName.trim()) fd.append("signer_name", signerName.trim());
      await api.post(`/jobs/${job.id}/legs/${legIndex}/complete`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(stopMeta.toast);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" data-testid="leg-complete-page">
      <PageHeader
        onBack={onBack}
        title={stopMeta.title}
        subtitle={leg.address + (leg.notes ? ` · ${leg.notes}` : "")}
      />

      <div className="max-w-lg space-y-5">
        {/* Photos */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <p className="text-white/85 mb-3 font-semibold text-sm">Photos ({files.length}/8)</p>
          <div className="grid grid-cols-4 gap-2">
            {files.map((f, i) => (
              <div key={i} className="relative aspect-square">
                <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover rounded-xl border border-white/10" />
                <button onClick={() => setFiles((a) => a.filter((_, idx) => idx !== i))}
                  className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center shadow-lg">×</button>
              </div>
            ))}
            {files.length < 8 && (
              <>
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-[#19e6c8]/60 hover:text-[#19e6c8]/60 transition-colors">
                  <Camera size={22} /><span className="text-[10px] mt-1 font-semibold">Camera</span>
                  <input type="file" accept="image/*" capture="environment" hidden onChange={onPick} />
                </label>
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-[#19e6c8]/60 hover:text-[#19e6c8]/60 transition-colors">
                  <Upload size={22} /><span className="text-[10px] mt-1 font-semibold">Upload</span>
                  <input type="file" accept="image/*" multiple hidden onChange={onPick} />
                </label>
              </>
            )}
          </div>
        </div>

        {/* Signer name */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <label className="block text-white/70 font-semibold mb-2 text-sm">{stopMeta.nameLabel}</label>
          <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
            placeholder={stopMeta.namePlaceholder}
            className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#19e6c8]/50 text-sm" />
        </div>

        {/* Signature */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/85 font-semibold text-sm">Signature</p>
            <button onClick={clearSig} className="text-xs text-white/50 hover:text-white flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-white/10">
              <Eraser size={13} /> Clear
            </button>
          </div>
          <canvas ref={sigRef} width={600} height={220}
            className="w-full rounded-xl bg-white border-2 border-white/20 cursor-crosshair touch-none" />
          <p className="text-[10px] text-white/30 mt-1.5 text-center">Draw signature above</p>
        </div>

        {/* Submit */}
        <button onClick={submit} disabled={busy}
          className="w-full px-5 py-4 rounded-2xl text-base font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
          style={{ background: "linear-gradient(90deg, #19e6c8, #2b5fff)", color: "#fff" }}>
          <Check size={18} /> {busy ? "Uploading…" : stopMeta.doneLabel}
        </button>
      </div>
    </div>
  );
}

// ── POD View ─────────────────────────────────────────────────────────────────
function PodView({ job, replace = false, onBack, onDone }) {
  const [files, setFiles] = useState([]);
  const [receiverName, setReceiverName] = useState("");
  const [driverNotes, setDriverNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);
  const { clear: clearSig } = useSignatureCanvas(sigRef);

  const onPick = (e) => {
    const sel = Array.from(e.target.files || []).slice(0, 8 - files.length);
    setFiles((f) => [...f, ...sel].slice(0, 8));
  };

  const submit = async () => {
    if (files.length === 0) { toast.error("Please add at least 1 POD photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", await compressImage(f));
      fd.append("signature_data_url", sigRef.current.toDataURL("image/png"));
      if (receiverName.trim()) fd.append("receiver_name", receiverName.trim());
      if (replace) fd.append("replace", "true");
      await api.post(`/jobs/${job.id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (driverNotes.trim()) {
        await api.patch(`/jobs/${job.id}/driver-notes`, { driver_notes: driverNotes.trim() });
      }
      toast.success("POD uploaded");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" data-testid="pod-page">
      <PageHeader
        onBack={onBack}
        title={replace ? "Re-upload POD" : "Proof of Delivery"}
        subtitle={`${job.job_ref} · ${replace ? "Replaces existing photos & signature." : "Up to 8 photos + receiver signature."}`}
      />

      <div className="max-w-lg space-y-5">
        {/* Photos grid */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <p className="text-white/85 mb-3 font-semibold text-sm">Photos ({files.length}/8)</p>
          <div className="grid grid-cols-4 gap-2">
            {files.map((f, i) => (
              <div key={i} className="relative aspect-square">
                <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover rounded-xl border border-white/10" />
                <button onClick={() => setFiles((arr) => arr.filter((_, idx) => idx !== i))}
                  className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center shadow-lg">×</button>
              </div>
            ))}
            {files.length < 8 && (
              <>
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-[#19e6c8]/60 hover:text-[#19e6c8]/60 transition-colors">
                  <Camera size={22} /><span className="text-[10px] mt-1 font-semibold">Camera</span>
                  <input type="file" accept="image/*" capture="environment" hidden onChange={onPick} data-testid="pod-camera" />
                </label>
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-[#19e6c8]/60 hover:text-[#19e6c8]/60 transition-colors">
                  <Upload size={22} /><span className="text-[10px] mt-1 font-semibold">Upload</span>
                  <input type="file" accept="image/*" multiple hidden onChange={onPick} data-testid="pod-upload" />
                </label>
              </>
            )}
          </div>
        </div>

        {/* Driver Notes */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5"
          style={{ borderColor: "rgba(59,130,246,0.25)", background: "rgba(59,130,246,0.05)" }}>
          <label className="block text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: "#3b82f6" }}>
            <StickyNote size={14} /> Completion Notes
            <span className="text-white/30 font-normal text-xs">(optional — admin only)</span>
          </label>
          <textarea
            value={driverNotes}
            onChange={(e) => setDriverNotes(e.target.value)}
            placeholder="Any notes about this delivery, issues, or observations…"
            rows={3}
            className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-400/50 resize-none"
            data-testid="driver-notes-input"
          />
        </div>

        {/* Receiver Name */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <label className="block text-white/70 font-semibold mb-2 text-sm">Receiver Name</label>
          <input value={receiverName} onChange={(e) => setReceiverName(e.target.value)} placeholder="Person receiving goods"
            className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#19e6c8]/50 text-sm" data-testid="receiver-name" />
        </div>

        {/* Signature */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/85 font-semibold text-sm">Receiver Signature</p>
            <button onClick={clearSig} className="text-xs text-white/50 hover:text-white flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-white/10" data-testid="clear-sig">
              <Eraser size={13} /> Clear
            </button>
          </div>
          <canvas ref={sigRef} width={600} height={220}
            className="w-full rounded-xl bg-white border-2 border-white/20 cursor-crosshair touch-none"
            data-testid="signature-pad" />
          <p className="text-[10px] text-white/30 mt-1.5 text-center">Draw signature above</p>
        </div>

        {/* Submit */}
        <button onClick={submit} disabled={busy}
          className="w-full px-5 py-4 rounded-2xl text-base font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
          style={{ background: "linear-gradient(90deg, #2b5fff, #3b82f6)", color: "#fff" }}
          data-testid="submit-pod">
          <Check size={18} /> {busy ? "Uploading…" : "Submit POD"}
        </button>
      </div>
    </div>
  );
}

// ── Re-upload View (photos + completion notes only, no signature) ──────────────
// Used for both pickup and delivery. Replaces the photos and updates the completion
// notes; existing signatures and job status are preserved server-side.
function ReuploadView({ job, kind, onBack, onDone }) {
  const isPickup = kind === "pickup";
  const maxPhotos = isPickup ? 4 : 8;
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState(job.driver_notes || "");
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const sel = Array.from(e.target.files || []).slice(0, maxPhotos - files.length);
    setFiles((f) => [...f, ...sel].slice(0, maxPhotos));
  };

  const submit = async () => {
    if (files.length === 0) { toast.error("Please add at least 1 photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", await compressImage(f));
      fd.append("replace", "true");
      const endpoint = isPickup ? `/jobs/${job.id}/pickup-signature` : `/jobs/${job.id}/pod`;
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (notes.trim() !== (job.driver_notes || "")) {
        await api.patch(`/jobs/${job.id}/driver-notes`, { driver_notes: notes.trim() });
      }
      toast.success(isPickup ? "Pickup photos updated" : "POD updated");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" data-testid={`reupload-${kind}-page`}>
      <PageHeader
        onBack={onBack}
        title={isPickup ? "Re-upload Pickup" : "Re-upload POD"}
        subtitle={`${job.job_ref} · Replaces photos & updates completion notes`}
      />

      <div className="max-w-lg space-y-5">
        {/* Photos */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5">
          <p className="text-white/85 mb-3 font-semibold text-sm">Photos ({files.length}/{maxPhotos})</p>
          <div className="grid grid-cols-4 gap-2">
            {files.map((f, i) => (
              <div key={i} className="relative aspect-square">
                <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover rounded-xl border border-white/10" />
                <button onClick={() => setFiles((arr) => arr.filter((_, idx) => idx !== i))}
                  className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center shadow-lg">×</button>
              </div>
            ))}
            {files.length < maxPhotos && (
              <>
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-[#a78bfa]/60 hover:text-[#a78bfa]/60 transition-colors">
                  <Camera size={22} /><span className="text-[10px] mt-1 font-semibold">Camera</span>
                  <input type="file" accept="image/*" capture="environment" hidden onChange={onPick} data-testid="reupload-camera" />
                </label>
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-[#a78bfa]/60 hover:text-[#a78bfa]/60 transition-colors">
                  <Upload size={22} /><span className="text-[10px] mt-1 font-semibold">Upload</span>
                  <input type="file" accept="image/*" multiple hidden onChange={onPick} data-testid="reupload-upload" />
                </label>
              </>
            )}
          </div>
        </div>

        {/* Completion Notes */}
        <div className="bg-[#15151f] border border-white/10 rounded-2xl p-5"
          style={{ borderColor: "rgba(59,130,246,0.25)", background: "rgba(59,130,246,0.05)" }}>
          <label className="block text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: "#3b82f6" }}>
            <StickyNote size={14} /> Completion Notes
            <span className="text-white/30 font-normal text-xs">(optional — admin only)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes, issues, or observations…"
            rows={3}
            className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-400/50 resize-none"
            data-testid="reupload-notes-input"
          />
        </div>

        {/* Submit */}
        <button onClick={submit} disabled={busy}
          className="w-full px-5 py-4 rounded-2xl text-base font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
          style={{ background: "linear-gradient(90deg, #6f4cff, #a78bfa)", color: "#fff" }}
          data-testid="submit-reupload">
          <Check size={18} /> {busy ? "Uploading…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
